// © Microsoft Corporation. All rights reserved.

import { AppError, ConfigOrigin, ConfigOrigins } from "@msnews/core-contracts";
import { ConfigReference, ExperienceConfig } from "@msnews/config-data-types";
import { defaultInstance, featureConfigs, featureManifests, gitConfigsTreatmentWithIdb, localizationConfigs } from "./Constants";

import { ConfigCacheProperties } from "@msnews/core-contracts";
import { getLocationSearch } from "@msnews/isomorphic-util";
import { mockManager } from "@msnews/mock-config";

export const buildNumberRegex = /^\d{8}\.\d+$/;

/**
 * Get the cache key based on configRef object
 * @param {ConfigReference} configRef object
 * @returns The unique cache key from configRef
 */
export function getCacheKeyFromConfigRef(configRef: ConfigReference, appType?: string): string {
    if (!configRef) {
        return;
    }

    const { experienceType, instanceSrc, sharedNs } = configRef;
    const keys = [appType, experienceType, instanceSrc];
    if (sharedNs) {
        keys.push(sharedNs);
    }
    return keys.map(key => key || "").join("/").toLowerCase();
}

/**
 * Map key received from CRS response to ConfigReference
 * @param key received in CRS response
 * @returns ConfigReference
 */
export function getConfigRefFromKey(key: string): ConfigReference {
    if (!key) {
        return null;
    }

    if (key.match("^shared/")) {
        const [sharedPrefix, sharedNs, experienceType, instanceSrc] = key.split("/");
        return { experienceType, instanceSrc, sharedNs };
    } else {
        const [experienceType, instanceSrc] = key.split("/");
        return { experienceType, instanceSrc };
    }
}

/**
 * This function returns child configRefs
 * @param {ExperienceConfig} resolvedConfig: Experience resolved properties
 * @returns {Set<ConfigReference>} Array of Config Refs in the resolved config.
 */
export function getChildConfigRefs(resolvedConfig: ExperienceConfig): Set<ConfigReference> {
    const configRefSet = new Set<ConfigReference>();
    if (resolvedConfig && resolvedConfig.properties) {
        try {
            addConfigRefFromObject(resolvedConfig.properties, configRefSet);
        } catch (error) {
            // Log Error
        }
    }

    return configRefSet;
}

/**
 * Adds all config ref found in the object to the given set.
 * @param obj The object to search in.
 * @param indicies The set of ConfigReference to add to.
 */
function addConfigRefFromObject(obj: any, indicies: Set<ConfigReference>): void {
    const configRef: any = obj.configRef;
    if (configRef && configRef.experienceType) {
        indicies.add(configRef);
    }

    for (const value of Object.values(obj)) {
        if (typeof (value) === "object" && value) {
            addConfigRefFromObject(value, indicies);
        }
    }
}

/**
 * Gets Entity Reference string from entity object.
 * This is a combination of experienceType, instanceSrc and sharedNs (if exists), to access manifest hashes.
 * @param {ConfigReference} configReference config reference for the root config.
 * @param {boolean} isLocalizationConfig indicates if this is for localizationConfigs.
 * @returns {Map<string, ExperienceConfig>} returns the merged config map.
 */
export function getConfigReferencePath(configReference: ConfigReference, isLocalizationConfig = false): string {
    if (!configReference) {
        return;
    }

    // use default instance when it is localization configs or when instanceSrc isn't available.
    const instance = (isLocalizationConfig ? defaultInstance : configReference.instanceSrc) || defaultInstance;
    if (!configReference.sharedNs || isLocalizationConfig) {
        return `${configReference.experienceType}/${instance}`;
    } else {
        return `shared/${configReference.sharedNs}/${configReference.experienceType}/${instance}`;
    }
}

/**
 * Race between network and cache promise
 * Borrowed from https://developers.google.com/web/fundamentals/instant-and-offline/offline-cookbook#cache-and-network-race
 * @param promises - Array of promises
 */
export function networkAndCacheRace(promises: Array<Promise<ExperienceConfig>>): Promise<ExperienceConfig> {
    // return a new promise
    return new Promise((resolve, reject) => {
        // iterate over each promise, 'chain' is the previously
        // returned value, or Promise.reject() for the first
        // iteration
        promises.reduce((chain: Promise<ExperienceConfig>, promise: Promise<ExperienceConfig>) => {
        // cast "promise" to a promise as other promise methods do
            promise = Promise.resolve(promise);
            // if this promise resolves, resolve the promise
            // we originally returned from networkAndCacheRace
            // (the resolve function ignore additional calls,
            // so first call wins)
            // Do nothing at catch because we want to wait for all promises.
            promise.then(resolve).catch(reject => {
                // do nothing
            });
            // build a chain of promises, where each catches from
            // the previous
            return chain.catch(() => {
                return promise;
            });
        }, Promise.reject()).catch(() => {
        // if we got here, none of the promises fulfilled
            reject(Error("Unable get a response from network or cache."));
        });
    });
}

/**
 * Return true if bundle, config and loc hash of current page is same as last page
 */
export function isConfigBundleLocHashSameAsLastPage(configCacheProperties: ConfigCacheProperties): boolean {
    if (!configCacheProperties) {
        return false;
    }

    const {bundleHash, lastBundleHash, configHash, lastConfigHash, locHash, lastLocHash} = configCacheProperties;
    if (lastBundleHash && lastBundleHash === bundleHash
        && lastConfigHash && lastConfigHash === configHash
        && lastLocHash && lastLocHash === locHash) {
        return true;
    }

    return false;
}

/**
 * Send this app error
 * @param appError AppError to send to telemetry
 */
export function sendAppError(appErrorCallback: (data: AppError) => void, appError: AppError): void {
    if (appErrorCallback) {
        appErrorCallback(appError);
    }
}

/**
 * Check if IndexedDB caching is enabled based on the flight treatment
 * @param flights Array of flights
 * @returns true if this is git configs treatment with indexedDB caching enabled
 */
export function enableIdbCaching(flights: string[]): boolean {
    return flights && flights.indexOf(gitConfigsTreatmentWithIdb) > -1;
}

/**
 * Get build number given associated app-type from published bundle info
 * eg: https://assets.msn.com/bundles/v1/edgeChromium/20210112.159/v, https://assets.msn.com/bundles/v1/edgeChromium/latest/v)
 * This is used only in local dev/TEST scenario. Please do not use this for PROD scenarios
 * @param buildVersion Build version
 * @param appType associated app type for that build
 * @returns Associated build number
 */
export async function getBuildNumberAsync(buildVersion: string, appType: string): Promise<string> {
    // Provide QSP build number override to buildVersion specified in bundleInfo
    let buildNumber: string;
    const itemParams = new URLSearchParams(getLocationSearch()).getAll("item");
    const itemSpalink = itemParams ?
        itemParams.find((param) => (param && param.indexOf("spalink:") === 0)) :
        undefined;
    if (itemSpalink) {
        buildNumber = itemSpalink.replace("spalink:", "").replace("/debug", "");
    }
    const build = buildNumber || buildVersion;
    if (!build || !appType || (build !== "latest" && build !== "rolling" &&
        // Check if build number doesn't look legit (eg: 20210112.159)
        !buildNumberRegex.test(build))) {
        throw new Error(`Arguments are incorrect. Check buildVersion: ${build} appType: ${appType}`);
    }
    // Adding a datetime qsp to ensure we don't get cached results for assets.msn.com/bundles/v1/rolling
    const fetchUrl = `https://assets.msn.com/bundles/v1/${appType}/${build}/v?${(new Date()).getTime()}`;
    let response: Response;
    try {
        response = await fetch(fetchUrl);
        if (!response || !response.ok) {
            throw new Error(`Response not OK, Status: ${response.status}, StatusText: ${response.statusText}`);
        }
        return (await response.text()).trim();
    } catch (error) {
        throw new Error(`getBuildNumberAsync Fetch Url: ${fetchUrl}, ${error}`);
    }
}

/**
 * Helper method that indicates whether configs matching the right config-type are from local
 * @param configType
 * @param configOrigins
 */
export function useLocalConfigs(configType: string, configOrigins: ConfigOrigins): boolean {
    return isFeatureConfigType(configType)
        ? isLocalOrigin(configOrigins && configOrigins.feature)
        : isLocalOrigin(configOrigins && configOrigins.loc);
}

/**
 * Helper method to indicate if config type is feature config related
 * @param configType
 */
function isFeatureConfigType(configType: string): boolean {
    return configType === featureConfigs || configType === featureManifests;
}

/**
 * Helper method to indicate if a specific config type (feature or loc) is local
 * @param configOrigin
 */
function isLocalOrigin(configOrigin: ConfigOrigin): boolean {
    return configOrigin === ConfigOrigin.Local;
}

/**
 * Helper method to set the right path to local file system in local dev work flow
 * @param configType loc-configs or feature-configs
 * @param configReference combination of experienceType, instance source and shared namespace
 * @param appType
 */
export function getLocalDevPrefix(configType: string, configReference: string, appType: string): string {
    let hasMockReplacement = false;
    const mockReplacement = mockManager.mocksEnabled
                            && mockManager.mockConfigDictionary
                            && mockManager.mockConfigDictionary[configReference];
    if (mockReplacement) {
        configReference = mockReplacement;
        hasMockReplacement = true;
    }

    let prependConfigType: string;
    if (hasMockReplacement) {
        prependConfigType = "mock";
    } else if (configType === localizationConfigs) {
        prependConfigType = "dev/loc";
    } else {
        prependConfigType = "prod";
    }
    const prependConfigLocation = /^shared/.test(configReference) ? configReference : `${appType}/${configReference}`;
    return `${prependConfigType}/${prependConfigLocation}`;
}